import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Mode = "light" | "dark" | "system";
type ColorTheme = "blue" | "green" | "orange" | "red" | "rose" | "violet" | "yellow" | "neutral";

interface ThemeContextValue {
	mode: Mode;
	setMode: (mode: Mode) => void;
	colorTheme: ColorTheme;
	setColorTheme: (theme: ColorTheme) => void;
	resolvedMode: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const MODE_KEY = "burresi-mode";
const COLOR_KEY = "burresi-color-theme";

function getSystemDark() {
	return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyMode(mode: Mode) {
	const isDark = mode === "dark" || (mode === "system" && getSystemDark());
	document.documentElement.classList.toggle("dark", isDark);
	return isDark ? "dark" : "light";
}

function applyColorTheme(theme: ColorTheme) {
	if (theme === "blue") {
		document.documentElement.removeAttribute("data-theme");
	} else {
		document.documentElement.setAttribute("data-theme", theme);
	}
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const [mode, setModeState] = useState<Mode>(() => {
		const stored = localStorage.getItem(MODE_KEY);
		return (stored as Mode) || "dark";
	});

	const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => {
		const stored = localStorage.getItem(COLOR_KEY);
		return (stored as ColorTheme) || "blue";
	});

	const [resolvedMode, setResolvedMode] = useState<"light" | "dark">(() => applyMode(mode));

	const setMode = useCallback((m: Mode) => {
		localStorage.setItem(MODE_KEY, m);
		setModeState(m);
		setResolvedMode(applyMode(m));
	}, []);

	const setColorTheme = useCallback((t: ColorTheme) => {
		localStorage.setItem(COLOR_KEY, t);
		setColorThemeState(t);
		applyColorTheme(t);
	}, []);

	// Apply on mount and when mode changes
	useEffect(() => {
		setResolvedMode(applyMode(mode));
	}, [mode]);

	useEffect(() => {
		applyColorTheme(colorTheme);
	}, [colorTheme]);

	// Listen for system preference changes when mode is "system"
	useEffect(() => {
		if (mode !== "system") return;
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = () => setResolvedMode(applyMode("system"));
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, [mode]);

	const value = useMemo(
		() => ({ mode, setMode, colorTheme, setColorTheme, resolvedMode }),
		[mode, setMode, colorTheme, setColorTheme, resolvedMode],
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
	const ctx = useContext(ThemeContext);
	if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
	return ctx;
}
